import React, { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, Music, Save, X, Loader2, Upload, ChevronDown, ChevronRight, GripVertical, AlertTriangle, CheckCircle2, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import AdminLayout from '@/components/admin/AdminLayout';
import FileUpload from '@/components/admin/FileUpload';
import { useSite } from '@/context/SiteContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const emptyPlaylist = {
  title: '',
  description: '',
  cover_image: '',
  tracks: []
};

const emptyTrack = {
  title: '',
  duration: 180,
  audio_file: '',
  image: '',
  description: ''
};

const AdminPlaylists = () => {
  const { playlists, createPlaylist, updatePlaylist, deletePlaylist, reorderPlaylists } = useSite();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);
  const [form, setForm] = useState(emptyPlaylist);
  const [loading, setLoading] = useState(false);
  const [uploadingTrack, setUploadingTrack] = useState(null);
  const [expandedTracks, setExpandedTracks] = useState({});
  const [draggedTrack, setDraggedTrack] = useState(null);
  const [fileStatus, setFileStatus] = useState({}); // Track which files exist on server
  const [playlistIssues, setPlaylistIssues] = useState({}); // Track which playlists have missing files
  const [reordering, setReordering] = useState(false);

  // Move playlist up or down
  const movePlaylist = async (playlistId, direction) => {
    const currentIds = playlists.map(p => p.id);
    const currentIndex = currentIds.indexOf(playlistId);
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    
    // Check bounds
    if (newIndex < 0 || newIndex >= currentIds.length) return;
    
    // Swap positions
    const newIds = [...currentIds];
    [newIds[currentIndex], newIds[newIndex]] = [newIds[newIndex], newIds[currentIndex]];
    
    setReordering(true);
    try {
      const success = await reorderPlaylists(newIds);
      if (success) {
        toast.success('Playlist order updated');
      } else {
        toast.error('Failed to update order');
      }
    } catch (error) {
      console.error('Reorder error:', error);
      toast.error('Failed to update order');
    } finally {
      // Always reset reordering state
      setReordering(false);
    }
  };

  // Check if files exist on the server
  const checkFileExists = async (url) => {
    if (!url) return false;
    try {
      // Use GET with Range header instead of HEAD (more reliable across CDNs)
      const response = await fetch(url, { 
        method: 'GET',
        headers: { 'Range': 'bytes=0-0' }
      });
      // 200 or 206 (partial content) both mean the file exists
      return response.ok || response.status === 206;
    } catch {
      return false;
    }
  };

  // Check all playlists for missing files on initial load
  useEffect(() => {
    const checkAllPlaylists = async () => {
      const issues = {};
      for (const playlist of playlists) {
        let missingCount = 0;
        for (const track of (playlist.tracks || [])) {
          if (track.audio_file) {
            const exists = await checkFileExists(track.audio_file);
            if (!exists) missingCount++;
          }
        }
        if (missingCount > 0) {
          issues[playlist.id] = missingCount;
        }
      }
      setPlaylistIssues(issues);
    };
    if (playlists.length > 0) {
      checkAllPlaylists();
    }
  }, [playlists]);

  // Check all track files when dialog opens
  useEffect(() => {
    if (dialogOpen && form.tracks.length > 0) {
      const checkAllFiles = async () => {
        const status = {};
        for (let i = 0; i < form.tracks.length; i++) {
          const track = form.tracks[i];
          if (track.audio_file) {
            status[`audio_${i}`] = await checkFileExists(track.audio_file);
          }
          if (track.image) {
            status[`image_${i}`] = await checkFileExists(track.image);
          }
        }
        setFileStatus(status);
      };
      checkAllFiles();
    } else {
      setFileStatus({});
    }
  }, [dialogOpen, form.tracks.length]);

  // Count missing files
  const getMissingFilesCount = () => {
    let missingAudio = 0;
    let missingImages = 0;
    form.tracks.forEach((track, i) => {
      if (track.audio_file && fileStatus[`audio_${i}`] === false) missingAudio++;
      if (track.image && fileStatus[`image_${i}`] === false) missingImages++;
    });
    return { missingAudio, missingImages };
  };

  // Toggle track expansion
  const toggleTrack = (index) => {
    setExpandedTracks(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Drag and drop handlers
  const handleDragStart = (e, index) => {
    setDraggedTrack(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedTrack === null || draggedTrack === index) return;
    
    const newTracks = [...form.tracks];
    const draggedItem = newTracks[draggedTrack];
    newTracks.splice(draggedTrack, 1);
    newTracks.splice(index, 0, draggedItem);
    
    setForm(prev => ({ ...prev, tracks: newTracks }));
    setDraggedTrack(index);
    
    // Update expanded state for moved tracks
    setExpandedTracks(prev => {
      const newExpanded = {};
      Object.keys(prev).forEach(key => {
        const oldIndex = parseInt(key);
        if (oldIndex === draggedTrack) {
          newExpanded[index] = prev[oldIndex];
        } else if (oldIndex >= Math.min(draggedTrack, index) && oldIndex <= Math.max(draggedTrack, index)) {
          newExpanded[oldIndex + (draggedTrack < index ? -1 : 1)] = prev[oldIndex];
        } else {
          newExpanded[oldIndex] = prev[oldIndex];
        }
      });
      return newExpanded;
    });
  };

  const handleDragEnd = () => {
    setDraggedTrack(null);
  };

  // Get stored credentials
  const getAuthHeader = () => {
    const stored = sessionStorage.getItem('adminCredentials');
    if (stored) {
      const { username, password } = JSON.parse(stored);
      return 'Basic ' + btoa(`${username}:${password}`);
    }
    return '';
  };

  const openCreate = () => {
    setSelectedPlaylist(null);
    setForm(emptyPlaylist);
    setDialogOpen(true);
  };

  const openEdit = (playlist) => {
    setSelectedPlaylist(playlist);
    setForm({
      title: playlist.title,
      description: playlist.description || '',
      cover_image: playlist.cover_image || '',
      tracks: playlist.tracks || []
    });
    setDialogOpen(true);
  };

  const openDelete = (playlist) => {
    setSelectedPlaylist(playlist);
    setDeleteDialogOpen(true);
  };

  const handleChange = (field) => (e) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleCoverImageChange = (url) => {
    setForm(prev => ({ ...prev, cover_image: url }));
  };

  const handleTrackChange = (index, field) => (e) => {
    const newTracks = [...form.tracks];
    if (field === 'duration') {
      // Parse duration from MM:SS format
      const parts = e.target.value.split(':');
      if (parts.length === 2) {
        newTracks[index].duration = parseInt(parts[0]) * 60 + parseInt(parts[1]);
      } else {
        newTracks[index].duration = parseInt(e.target.value) || 0;
      }
    } else {
      newTracks[index][field] = e.target.value;
    }
    setForm(prev => ({ ...prev, tracks: newTracks }));
  };

  const handleTrackAudioUpload = async (index, file) => {
    setUploadingTrack(index);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_URL}/api/admin/upload/music`, {
        method: 'POST',
        headers: {
          'Authorization': getAuthHeader()
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        // Store relative URL - this ensures it works regardless of domain
        const relativeUrl = data.url;
        
        const newTracks = [...form.tracks];
        newTracks[index].audio_file = relativeUrl;
        // Update duration from server response (auto-detected by ffprobe)
        if (data.duration && data.duration > 0) {
          newTracks[index].duration = data.duration;
        }
        setForm(prev => ({ ...prev, tracks: newTracks }));
        // Mark this file as existing (just uploaded)
        setFileStatus(prev => ({...prev, [`audio_${index}`]: true}));
        
        toast.success(`${file.name} uploaded successfully`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Upload failed. Please try again.');
    } finally {
      setUploadingTrack(null);
    }
  };

  const addTrack = () => {
    setForm(prev => ({
      ...prev,
      tracks: [...prev.tracks, { ...emptyTrack, id: Date.now().toString() }]
    }));
  };

  const removeTrack = (index) => {
    setForm(prev => ({
      ...prev,
      tracks: prev.tracks.filter((_, i) => i !== index)
    }));
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      toast.error('Please enter a title');
      return;
    }

    setLoading(true);
    
    if (selectedPlaylist) {
      const success = await updatePlaylist(selectedPlaylist.id, form);
      if (success) {
        toast.success('Playlist updated');
        setDialogOpen(false);
      } else {
        toast.error('Failed to update playlist');
      }
    } else {
      const result = await createPlaylist(form);
      if (result) {
        toast.success('Playlist created');
        setDialogOpen(false);
      } else {
        toast.error('Failed to create playlist');
      }
    }
    
    setLoading(false);
  };

  const handleDelete = async () => {
    if (!selectedPlaylist) return;
    
    const success = await deletePlaylist(selectedPlaylist.id);
    if (success) {
      toast.success('Playlist deleted');
    } else {
      toast.error('Failed to delete playlist');
    }
    setDeleteDialogOpen(false);
  };

  return (
    <AdminLayout title="Music / Playlists">
      <div className="space-y-6">
        {/* Global Missing Files Warning */}
        {Object.keys(playlistIssues).length > 0 && (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-amber-500">Action Required: Missing Audio Files</p>
                <p className="text-sm text-muted-foreground mt-1">
                  {Object.values(playlistIssues).reduce((a, b) => a + b, 0)} audio file(s) need to be re-uploaded across {Object.keys(playlistIssues).length} playlist(s).
                  Click "Edit" on any playlist with a warning badge to see which tracks need attention.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-serif text-2xl">Your Music</h2>
            <p className="text-muted-foreground text-sm">
              Manage your playlists and albums. Use arrows to reorder.
            </p>
          </div>
          <Button onClick={openCreate} className="bg-foreground text-background hover:bg-foreground/90">
            <Plus className="h-4 w-4 mr-2" />
            Add Playlist
          </Button>
        </div>

        {/* Playlists grid */}
        {playlists.length === 0 ? (
          <Card className="bg-card border-border">
            <CardContent className="py-16 text-center">
              <Music className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="font-serif text-xl mb-2">No playlists yet</h3>
              <p className="text-muted-foreground mb-6">Create your first playlist to showcase your music</p>
              <Button onClick={openCreate} variant="outline">
                <Plus className="h-4 w-4 mr-2" />
                Create Playlist
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {playlists.map((playlist, index) => (
              <Card 
                key={playlist.id} 
                className={`bg-card border-border overflow-hidden transition-all ${playlistIssues[playlist.id] ? 'ring-2 ring-amber-500/50' : ''} ${reordering ? 'opacity-50 pointer-events-none' : ''}`}
              >
                <div className="aspect-square relative bg-muted">
                  {/* Reorder buttons */}
                  <div className="absolute top-2 left-2 z-10 flex flex-col gap-1">
                    <Button
                      variant="secondary"
                      size="icon"
                      className="h-7 w-7 bg-background/80 backdrop-blur-sm hover:bg-background"
                      onClick={() => movePlaylist(playlist.id, 'up')}
                      disabled={index === 0 || reordering}
                    >
                      <ChevronUp className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="secondary"
                      size="icon"
                      className="h-7 w-7 bg-background/80 backdrop-blur-sm hover:bg-background"
                      onClick={() => movePlaylist(playlist.id, 'down')}
                      disabled={index === playlists.length - 1 || reordering}
                    >
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                  </div>
                  {/* Order number */}
                  <div className="absolute top-2 left-12 z-10 bg-background/80 backdrop-blur-sm rounded px-2 py-1 text-xs font-medium">
                    #{index + 1}
                  </div>
                  {playlist.cover_image ? (
                    <img 
                      src={playlist.cover_image} 
                      alt={playlist.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Music className="h-12 w-12 text-muted-foreground" />
                    </div>
                  )}
                  {/* Missing files badge */}
                  {playlistIssues[playlist.id] && (
                    <div className="absolute top-2 right-2 bg-amber-500 text-white text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" />
                      {playlistIssues[playlist.id]} missing
                    </div>
                  )}
                </div>
                <CardContent className="p-4">
                  <h3 className="font-serif text-lg mb-1">{playlist.title}</h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    {playlist.tracks?.length || 0} tracks
                  </p>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => openEdit(playlist)}>
                      <Pencil className="h-3 w-3 mr-1" />
                      Edit
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => openDelete(playlist)} className="text-destructive hover:text-destructive">
                      <Trash2 className="h-3 w-3 mr-1" />
                      Delete
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-card border-border">
            <DialogHeader>
              <DialogTitle className="font-serif text-xl">
                {selectedPlaylist ? 'Edit Playlist' : 'Create Playlist'}
              </DialogTitle>
              <DialogDescription>
                {selectedPlaylist ? 'Update your playlist details and tracks' : 'Add a new playlist with cover art and audio tracks'}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6 py-4">
              <div className="space-y-2">
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  value={form.title}
                  onChange={handleChange('title')}
                  placeholder="Album/Playlist title"
                  className="bg-secondary border-border"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={form.description}
                  onChange={handleChange('description')}
                  placeholder="Brief description..."
                  rows={3}
                  className="bg-secondary border-border resize-none"
                />
              </div>

              <FileUpload
                type="image"
                label="Cover Image"
                value={form.cover_image}
                onChange={handleCoverImageChange}
              />

              {/* Tracks */}
              <div className="space-y-4">
                <Label>Tracks ({form.tracks.length})</Label>
                
                {/* Missing Files Warning Banner */}
                {(() => {
                  const { missingAudio, missingImages } = getMissingFilesCount();
                  if (missingAudio > 0 || missingImages > 0) {
                    return (
                      <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="font-medium text-amber-500">Missing Files Detected</p>
                            <p className="text-sm text-muted-foreground mt-1">
                              {missingAudio > 0 && <span className="block">• {missingAudio} audio file{missingAudio > 1 ? 's' : ''} need to be re-uploaded</span>}
                              {missingImages > 0 && <span className="block">• {missingImages} image{missingImages > 1 ? 's' : ''} need to be re-uploaded</span>}
                            </p>
                            <p className="text-xs text-muted-foreground mt-2">
                              Look for tracks marked with <AlertTriangle className="h-3 w-3 text-amber-500 inline" /> below
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}
                
                {form.tracks.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No tracks yet. Add your first track below.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {form.tracks.map((track, index) => (
                      <div
                        key={track.id || index}
                        draggable
                        onDragStart={(e) => handleDragStart(e, index)}
                        onDragOver={(e) => handleDragOver(e, index)}
                        onDragEnd={handleDragEnd}
                        className={`bg-secondary rounded-lg overflow-hidden transition-opacity ${draggedTrack === index ? 'opacity-50' : ''}`}
                      >
                        <Collapsible open={expandedTracks[index]} onOpenChange={() => toggleTrack(index)}>
                          {/* Collapsed Header */}
                          <div className="flex items-center gap-2 p-3">
                            <div 
                              className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground"
                              onMouseDown={(e) => e.stopPropagation()}
                            >
                              <GripVertical className="h-5 w-5" />
                            </div>
                            <CollapsibleTrigger asChild>
                              <button className="flex-1 flex items-center gap-3 text-left hover:bg-background/50 rounded p-1 -m-1">
                                {expandedTracks[index] ? (
                                  <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                                ) : (
                                  <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                                )}
                                <span className="text-sm font-medium text-muted-foreground w-6">#{index + 1}</span>
                                <span className="flex-1 truncate font-medium">
                                  {track.title || 'Untitled Track'}
                                </span>
                                <span className="text-sm text-muted-foreground">
                                  {formatDuration(track.duration)}
                                </span>
                                {/* File status indicators */}
                                {track.audio_file && fileStatus[`audio_${index}`] === false ? (
                                  <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0" title="Audio file missing - needs re-upload" />
                                ) : track.audio_file && fileStatus[`audio_${index}`] === true ? (
                                  <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" title="Audio file OK" />
                                ) : track.audio_file ? (
                                  <Loader2 className="h-4 w-4 text-muted-foreground animate-spin flex-shrink-0" title="Checking..." />
                                ) : (
                                  <Music className="h-4 w-4 text-muted-foreground/30 flex-shrink-0" title="No audio file" />
                                )}
                              </button>
                            </CollapsibleTrigger>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-8 w-8 text-muted-foreground hover:text-destructive flex-shrink-0"
                              onClick={() => removeTrack(index)}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                          
                          {/* Expanded Content */}
                          <CollapsibleContent>
                            <div className="px-4 pb-4 space-y-4 border-t border-border/50 pt-4">
                              {/* Title and Duration */}
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div className="space-y-1">
                                  <Label className="text-xs text-muted-foreground">Title</Label>
                                  <Input
                                    value={track.title}
                                    onChange={handleTrackChange(index, 'title')}
                                    placeholder="Track title"
                                    className="bg-background border-border"
                                  />
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-xs text-muted-foreground">Duration (auto-detected)</Label>
                                  <Input
                                    value={formatDuration(track.duration)}
                                    onChange={handleTrackChange(index, 'duration')}
                                    placeholder="3:30"
                                    className="bg-background border-border"
                                  />
                                </div>
                              </div>

                              {/* Track Description */}
                              <div className="space-y-1">
                                <Label className="text-xs text-muted-foreground">Track Description</Label>
                                <Textarea
                                  value={track.description || ''}
                                  onChange={handleTrackChange(index, 'description')}
                                  placeholder="Description for this track..."
                                  rows={2}
                                  className="bg-background border-border resize-none text-sm"
                                />
                              </div>

                              {/* Track Image */}
                              <div className="space-y-1">
                                <Label className="text-xs text-muted-foreground">Track Image</Label>
                                <div className="flex items-start gap-3">
                                  {track.image ? (
                                    fileStatus[`image_${index}`] === false ? (
                                      // Image is missing
                                      <div className="relative w-16 h-16 rounded-lg overflow-hidden bg-amber-500/10 border border-amber-500/30 flex-shrink-0 flex items-center justify-center">
                                        <AlertTriangle className="h-6 w-6 text-amber-500" />
                                      </div>
                                    ) : (
                                      <div className="relative w-16 h-16 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                                        <img src={track.image} alt="Track" className="w-full h-full object-cover" />
                                        <Button
                                          variant="destructive"
                                          size="icon"
                                          className="absolute top-1 right-1 h-5 w-5"
                                          onClick={() => {
                                            const newTracks = [...form.tracks];
                                            newTracks[index].image = '';
                                            setForm(prev => ({ ...prev, tracks: newTracks }));
                                            setFileStatus(prev => {
                                              const newStatus = {...prev};
                                              delete newStatus[`image_${index}`];
                                              return newStatus;
                                            });
                                          }}
                                        >
                                          <X className="h-3 w-3" />
                                        </Button>
                                      </div>
                                    )
                                  ) : null}
                                  <div className="flex flex-col gap-2">
                                    {track.image && fileStatus[`image_${index}`] === false && (
                                      <span className="text-xs text-amber-500 font-medium">Image missing - please re-upload</span>
                                    )}
                                    <input
                                      type="file"
                                      accept="image/*"
                                      onChange={async (e) => {
                                        const file = e.target.files?.[0];
                                        if (file) {
                                          const formData = new FormData();
                                          formData.append('file', file);
                                          try {
                                            const response = await fetch(`${API_URL}/api/admin/upload/image`, {
                                              method: 'POST',
                                              headers: { 'Authorization': getAuthHeader() },
                                              body: formData
                                            });
                                            if (response.ok) {
                                              const data = await response.json();
                                              const newTracks = [...form.tracks];
                                              // Store relative URL for domain independence
                                              newTracks[index].image = data.url;
                                              setForm(prev => ({ ...prev, tracks: newTracks }));
                                              setFileStatus(prev => ({...prev, [`image_${index}`]: true}));
                                              toast.success('Image uploaded');
                                            }
                                          } catch (err) {
                                            toast.error('Failed to upload image');
                                          }
                                        }
                                      }}
                                      className="hidden"
                                      id={`track-image-${index}`}
                                    />
                                    <Button
                                      variant={track.image && fileStatus[`image_${index}`] === false ? "default" : "outline"}
                                      size="sm"
                                      className={track.image && fileStatus[`image_${index}`] === false ? "bg-amber-500 hover:bg-amber-600 text-white" : ""}
                                      onClick={() => document.getElementById(`track-image-${index}`)?.click()}
                                    >
                                      <Upload className="h-3 w-3 mr-1" />
                                      {track.image && fileStatus[`image_${index}`] === false ? 'Re-upload Image' : track.image ? 'Change' : 'Upload Image'}
                                    </Button>
                                  </div>
                                </div>
                              </div>
                              
                              {/* Audio file upload */}
                              <div className="space-y-1">
                                <Label className="text-xs text-muted-foreground">Audio File</Label>
                                {track.audio_file ? (
                                  fileStatus[`audio_${index}`] === false ? (
                                    // File is missing - show warning and re-upload option
                                    <div className="space-y-2">
                                      <div className="flex items-center gap-2 p-3 bg-amber-500/10 border border-amber-500/30 rounded">
                                        <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0" />
                                        <span className="text-sm text-amber-500 font-medium">Audio file missing - please re-upload</span>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <input
                                          type="file"
                                          accept="audio/*,.mp3,.wav,.ogg,.m4a"
                                          onChange={(e) => {
                                            const file = e.target.files?.[0];
                                            if (file) handleTrackAudioUpload(index, file);
                                          }}
                                          className="hidden"
                                          id={`track-audio-reupload-${index}`}
                                        />
                                        <Button
                                          variant="default"
                                          size="sm"
                                          className="bg-amber-500 hover:bg-amber-600 text-white"
                                          onClick={() => document.getElementById(`track-audio-reupload-${index}`)?.click()}
                                          disabled={uploadingTrack === index}
                                        >
                                          {uploadingTrack === index ? (
                                            <>
                                              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                              Uploading...
                                            </>
                                          ) : (
                                            <>
                                              <Upload className="h-3 w-3 mr-1" />
                                              Re-upload Audio
                                            </>
                                          )}
                                        </Button>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => {
                                            const newTracks = [...form.tracks];
                                            newTracks[index].audio_file = '';
                                            setForm(prev => ({ ...prev, tracks: newTracks }));
                                            setFileStatus(prev => {
                                              const newStatus = {...prev};
                                              delete newStatus[`audio_${index}`];
                                              return newStatus;
                                            });
                                          }}
                                        >
                                          <X className="h-3 w-3 mr-1" />
                                          Clear
                                        </Button>
                                      </div>
                                    </div>
                                  ) : (
                                    // File exists - show normal audio player
                                    <div className="flex items-center gap-3 p-2 bg-background rounded">
                                      <Music className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                                      <audio controls className="flex-1 h-8">
                                        <source src={track.audio_file} />
                                      </audio>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8 flex-shrink-0"
                                        onClick={() => {
                                          const newTracks = [...form.tracks];
                                          newTracks[index].audio_file = '';
                                          setForm(prev => ({ ...prev, tracks: newTracks }));
                                          setFileStatus(prev => {
                                            const newStatus = {...prev};
                                            delete newStatus[`audio_${index}`];
                                            return newStatus;
                                          });
                                        }}
                                      >
                                        <X className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  )
                                ) : (
                                  <div className="flex items-center gap-2">
                                    <input
                                      type="file"
                                      accept="audio/*,.mp3,.wav,.ogg,.m4a"
                                      onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        if (file) handleTrackAudioUpload(index, file);
                                      }}
                                      className="hidden"
                                      id={`track-audio-${index}`}
                                    />
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => document.getElementById(`track-audio-${index}`)?.click()}
                                      disabled={uploadingTrack === index}
                                    >
                                      {uploadingTrack === index ? (
                                        <>
                                          <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                          Uploading...
                                        </>
                                      ) : (
                                        <>
                                          <Upload className="h-3 w-3 mr-1" />
                                          Upload Audio
                                        </>
                                      )}
                                    </Button>
                                    <span className="text-xs text-muted-foreground">MP3, WAV, OGG, M4A</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </CollapsibleContent>
                        </Collapsible>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Add Track Button */}
                <div className="pt-4">
                  <Button variant="outline" className="w-full" onClick={addTrack}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Track
                  </Button>
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="ghost" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSave} 
                className="bg-foreground text-background hover:bg-foreground/90"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    {selectedPlaylist ? 'Save Changes' : 'Create Playlist'}
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete confirmation */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent className="bg-card border-border">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Playlist</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete "{selectedPlaylist?.title}"? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDelete}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
};

export default AdminPlaylists;
